import { useEffect, useMemo, useRef, useState } from 'react';
import { accounts, type AuthProblem } from '../backend';
import { useApp } from '../store';
import { confirmDialog, promptDialog } from '../confirm';
import { boardsForPerson } from '../projects';
import { inviteProblem, invitationText, signInTarget, targetIsElsewhere } from '../invite';
import { can, ROLE_LABEL, ROLE_BLURB, ROLES, PROJECT_ROLES, CAPABILITY_LABEL, type ProjectRole, type Role } from '../permissions';
import { displayName, initials, isNamed, nameFromEmail } from '../people';
import VendorTag from './VendorTag';
import {
  COMPANY_ROLE_PICK,
  COMPANY_ROLE_LABEL,
  CARD_STANCE_ORDER,
  STANCE_LABEL,
  type CompanyRole,
  type CardStance,
} from '../company-role';
import { canPreview } from '../viewas';
import { AUDIT_LABEL, issueFromSnapshot, type AuditEntry, type AuditKind, type AuditOrigin } from '../audit';
import { asFile, changeFacts, fold, NOTHING_SIFTED, originPlace, sift, type Sift } from '../history';
import { A_PAGE, at } from '../paging';
import { PageSize, Pages } from './Pages';
import { remember } from '../trail';
import { asDate, historyTime, relativeTime } from '../utils';
import Info from './Info';
import Icon from './Icon';
import PersonAccess from './PersonAccess';
import { latestSignInByPerson, mergeSignInTimes } from '../sessions';
import { useTabKeys } from './useTabKeys';
import AddPerson from './AddPerson';
import { RequestToAdd, RequestsQueue } from './RequestAccess';
import Groups from './Groups';
import { TEAMS_ARE_CONNECTED } from '../groups';

type Tab = 'people' | 'requests' | 'roles' | 'groups' | 'audit';

/* The order the roles read across the "what each role can do" matrix (and the
   legend cards above it): the external VENDOR first, then the internal ladder
   from least to most privileged. Display order only — the shared ROLES list
   keeps its own order for the logic that depends on it. Any role in ROLES that
   is not named here is appended, so a role added later still appears (at the
   end) rather than silently dropping off the matrix. */
const ROLE_ORDER: Role[] = ['vendor', 'viewer', 'reviewer', 'member', 'manager', 'admin'];
const ROLE_COLUMNS: Role[] = [
  ...ROLE_ORDER.filter((r) => ROLES.includes(r)),
  ...ROLES.filter((r) => !ROLE_ORDER.includes(r)),
];

const TABS: { id: Tab; label: string; adminOnly?: boolean; needsTeams?: boolean }[] = [
  { id: 'people', label: 'People' },
  /* Deciding who joins is the other half of managing people, so it lives here
     as a tab rather than as a floating admin-only destination of its own —
     "Requests" sat one nav item from "Sign-offs" and shared a word with the
     old "Approvers" settings tab (now "Sign-off"), three look-alike names for
     two unrelated jobs. Admin-only; the pending count badges the "People and
     access" nav item and, when something is waiting, this is the tab the screen
     opens on. The far side an admin answers is RequestsQueue; RequestToAdd is
     the ask itself. */
  { id: 'requests', label: 'Requests', adminOnly: true },
  { id: 'roles', label: 'Roles' },
  /* Was "Projects", which described what it listed rather than what it is
     for. It answers "what can this person reach, and what may they do there",
     which is access. */
  /* THERE WAS AN "ACCESS" TAB HERE, and removing it was the point. Managing
     one person was split across three screens — their name and role here,
     their boards there, their teams somewhere else — and every one of them
     began by asking WHICH PERSON. That is the same question answered three
     times. A person is one subject; everything about them is now in their own
     panel below.
     Teams stay, because one team and its people is a different subject rather
     than the same one split up. Admin only: putting somebody in a team grants
     every board that team is named on. */
  /* HIDDEN while `TEAMS_ARE_CONNECTED` is false — the tab wrote nothing and
     said it had. See src/groups.ts, which is the only switch. */
  { id: 'groups', label: 'Teams', adminOnly: true, needsTeams: true },
  // The trail names who did what. A project manager runs their board; watching
  // people is a workspace matter.
  { id: 'audit', label: 'History', adminOnly: true },
];

/**
 * Managing everyone else — deliberately not in Settings.
 *
 * Settings is where this project is configured; who may open it and what they
 * may do once inside is a different question with a different audience, and it
 * needs room to become granular. Reached from your own avatar rather than a
 * seventh bottom tab, which the phone layout has no space for.
 */
export default function Admin({ initialTab }: { initialTab?: Tab } = {}) {
  const meta = useApp((s) => s.meta);
  /*
   * YOUR REAL ROLE, not the previewed one — the only screen that uses it.
   *
   * Every other screen reads `shownRole`, which is the point of the preview.
   * This one may not: previewing a viewer would hide People and access, and
   * People and access is where the preview is started and stopped. The banner
   * can always end it, but a screen that can hide the way out of itself is a
   * trap, and this is that screen.
   */
  const role = useApp((s) => s.role);
  const access = useApp((s) => s.access);
  const roles = useApp((s) => s.roles);
  const legacyRoles = useApp((s) => s.legacyRoles);
  const setPersonAdmin = useApp((s) => s.setPersonAdmin);
  const audit = useApp((s) => s.audit);
  const auditOrigin = useApp((s) => s.auditOrigin);
  const recordAudit = useApp((s) => s.recordAudit);
  const profiles = useApp((s) => s.profiles);
  const myEmail = useApp((s) => s.user?.email?.toLowerCase() ?? '');
  const setPersonRole = useApp((s) => s.setPersonRole);
  const setPersonProfile = useApp((s) => s.setPersonProfile);
  const resetPassword = useApp((s) => s.resetPassword);
  const changeEmail = useApp((s) => s.changeEmail);
  const setSuspended = useApp((s) => s.setSuspended);
  const groupsOf = useApp((s) => s.groupsOf);
  const removePerson = useApp((s) => s.removePerson);
  const setView = useApp((s) => s.setView);
  const previewAs = useApp((s) => s.previewAs);
  const boardRequests = useApp((s) => s.boardRequests);
  // LAST SIGNED IN per person (people-access addition A). The `sessions` table is
  // private even from admins (policy sessions_own = lower(email)=me(), 0002), on
  // purpose — a list of someone's machines and places is not workspace
  // information. So a privacy-preserving admin-only RPC (migration 0210) supplies
  // every person's last-signed-in TIME, and nothing else, which is all the roster
  // needs. We merge: the viewer's own sessions give their own time even without
  // the RPC (a non-admin, or a fake backend), and the RPC — authoritative, and
  // including the viewer — fills in everyone else for an admin.
  const sessions = useApp((s) => s.sessions);
  const signInTimes = useApp((s) => s.signInTimes);
  const [signInRows, setSignInRows] = useState<{ email: string; at: number }[]>([]);
  useEffect(() => {
    let live = true;
    signInTimes().then((rows) => {
      if (live) setSignInRows(rows);
    });
    return () => {
      live = false;
    };
  }, [signInTimes]);
  const lastSignIn = useMemo(
    () => mergeSignInTimes(latestSignInByPerson(sessions), signInRows),
    [sessions, signInRows]
  );
  /* The count that badges the Requests tab and the nav item alike. */
  const pendingRequests = boardRequests.filter((r) => r.status === 'pending').length;
  /* Opens on Requests when something is waiting on an admin — the reason a
     badge is showing at all — otherwise on People. An explicit initialTab (the
     legacy `requests` route redirects here) always wins. */
  const [tab, setTab] = useState<Tab>(
    () => initialTab ?? (can(role, 'manageWorkspace') && pendingRequests > 0 ? 'requests' : 'people')
  );
  // Hiding a tab is not the same as leaving it. Someone whose rights are reduced
  // while they are looking at an admin-only tab must be moved off it, not merely
  // denied the way back. The database refuses them the data either way, but a
  // screen that keeps showing a heading it may not show is its own kind of wrong.
  const visibleTabs = TABS.filter(
    (t) => (!t.adminOnly || can(role, 'manageWorkspace')) && (!t.needsTeams || TEAMS_ARE_CONNECTED)
  );
  const current = visibleTabs.some((t) => t.id === tab) ? tab : 'people';
  const tabKeys = useTabKeys(visibleTabs.map((t) => t.id), current, setTab);
  /** Whether the Add someone page is showing instead of this one. */
  const [addingSomeone, setAddingSomeone] = useState(false);
  const [findPerson, setFindPerson] = useState('');
  /** The roster filter (the .seg): everyone, or one standing. */
  const [band, setBand] = useState<'all' | 'admins' | 'members' | 'vendors' | 'invited'>('all');
  /** Whose ⋯ account menu is open (one at a time), and whose temporary password
      a reset just issued — shown once in the handed card, stored nowhere. */
  const [menuFor, setMenuFor] = useState('');
  const [resetHanded, setResetHanded] = useState<{ email: string; password: string } | null>(null);
  const [copiedPw, setCopiedPw] = useState(false);
  /* The open ⋯ menu wrapper, so a click anywhere outside it (menu + its own
     kebab) closes it, as does Escape — the standing menu rule (R4). */
  const menuWrapRef = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    if (!menuFor) return;
    const onDown = (ev: MouseEvent) => {
      if (!menuWrapRef.current?.contains(ev.target as Node)) setMenuFor('');
    };
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') setMenuFor('');
    };
    // mousedown (not click) so selecting text inside and releasing outside is not
    // a dismissal, and so the nearest empty space still closes it.
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuFor]);
  // Only used when exactly one address is being added — see the fields below.
  const [err, setErr] = useState('');
  const [inviting, setInviting] = useState('');
  const [invited, setInvited] = useState<Record<string, string>>({});
  const [editing, setEditing] = useState('');
  /** Which deletion is being put back, so its own button says so. */
  const [restoring, setRestoring] = useState('');
  /** Which invitation was just copied, so its own button says so. */
  const [copied, setCopied] = useState('');
  const projects = useApp((s) => s.projects);
  const projectId = useApp((s) => s.projectId);
  const restoreIssue = useApp((s) => s.restoreIssue);

  // Firebase sends this itself — no mail server, no backend, nothing to pay for.
  const sendInvite = async (address: string) => {
    setInviting(address);
    try {
      await accounts.sendSignInLink(address, signInTarget());
      setInvited((prev) => ({ ...prev, [address]: 'sent' }));
    } catch (e) {
      // Said in words, not as a provider's code. "failed —
      // auth/unauthorized-continue-uri" reads as a fault in the board; it is
      // one setting on one screen, and inviteProblem says which.
      setInvited((prev) => ({
        ...prev,
        [address]: inviteProblem(e as { reason?: AuthProblem; code?: string }, signInTarget()),
      }));
    } finally {
      setInviting('');
    }
  };

  /**
   * Every one of these was a floating promise, so a refused write did nothing at
   * all and said nothing at all. Adding somebody touches the most guarded
   * document on the board, and refusal there is a normal outcome — a button that
   * quietly stops working is the worst possible way to report it.
   */
  const report = (p: Promise<unknown>, done?: () => void) =>
    void p.then(
      () => {
        setErr('');
        done?.();
      },
      (e: unknown) => {
        const why = e instanceof Error ? e.message : String(e);
        // Firestore's own message ends in a full stop, so appending a clause
        // to it read as "…permissions. — you may not be allowed to do that."
        const said = why.replace(/\.\s*$/, '');
        setErr(/permission/i.test(why) ? `${said} — you may not be allowed to do that.` : why);
      }
    );

  /*
   * ONE ADDRESS OR FORTY, THROUGH THE SAME BOX.
   *
   * Adding a development team one at a time is twenty presses and twenty
   * chances to mistype. What people actually have is a list — pasted out of an
   * email, a spreadsheet column, a chat message — so the box takes all of them
   * and parseAddresses sorts out the separators.
   *
   * ONE AT A TIME UNDERNEATH, deliberately. Each person is a separate write and
   * the rules judge each on its own; batching them into one write would mean
   * one refusal losing the other thirty-nine. So the ones that work go in, and
   * the ones that do not are named.
   */

  /*
   * Matched on BOTH the name and the address, because people search for
   * whichever they happen to know. Somebody looking for "Dominic" should not
   * have to remember he is dominic.smart@trinre.com.
   */
  const shownPeople = access.emails.filter((e) => {
    const q = findPerson.trim().toLowerCase();
    return !q || e.toLowerCase().includes(q) || displayName(e, profiles).toLowerCase().includes(q);
  });

  /* WHO SOMEBODY IS, decided once, so the roster groups and the counts never
     disagree. An admin is an admin; a vendor is anyone whose company role is
     vendor (a vendor can never be an admin); everyone else is an internal member. */
  const standingOf = (e: string): 'admin' | 'vendor' | 'member' => {
    if (access.admins.includes(e) || legacyRoles[e] === 'admin') return 'admin';
    if (profiles[e]?.companyRole === 'vendor') return 'vendor';
    return 'member';
  };
  const isInvited = (e: string) => !profiles[e]?.passwordSetAt;

  /* The band (the .seg filter). Applied to the full list; the three group bands
     are each then rendered from it, so "Admins" and "Invited" alike just narrow
     which rows survive. */
  const inBand = (e: string): boolean => {
    const s = standingOf(e);
    switch (band) {
      case 'admins': return s === 'admin';
      case 'members': return s === 'member';
      case 'vendors': return s === 'vendor';
      case 'invited': return isInvited(e);
      default: return true;
    }
  };
  const visible = shownPeople.filter(inBand);
  const admins = visible.filter((e) => standingOf(e) === 'admin');
  const members = visible.filter((e) => standingOf(e) === 'member');
  const vendors = visible.filter((e) => standingOf(e) === 'vendor');

  /* The summary counts read the WHOLE workspace, not the filtered view — they are
     the standing shape of the place, not a result of the current filter. */
  const countAdmins = access.emails.filter((e) => standingOf(e) === 'admin').length;
  const countMembers = access.emails.filter((e) => standingOf(e) === 'member').length;
  const countVendors = access.emails.filter((e) => standingOf(e) === 'vendor').length;
  const countInvited = access.emails.filter(isInvited).length;

  /*
   * REACH — the boards a person can actually open, cross-board (projects +
   * membership + teams), the same source PersonAccess reads. Answers "who can do
   * what" at a glance, and is ALSO the set the Copy-invitation note names (#3), so
   * the note can never again name only the board that happens to be open.
   */
  const boardsOpenTo = (email: string, isTheirAdmin: boolean) =>
    boardsForPerson({ projects, email, isAdmin: isTheirAdmin, filter: '', groupsOf }).on;
  const reachLabel = (email: string, isTheirAdmin: boolean, isVendor: boolean): string => {
    if (isTheirAdmin) return 'Every board';
    const on = boardsOpenTo(email, isTheirAdmin);
    if (on.length === 0) return 'No boards';
    // A vendor confined to one board reads better named — "CSM only" — than counted.
    if (isVendor && on.length === 1) return `${on[0].key} only`;
    return `${on.length} ${on.length === 1 ? 'board' : 'boards'}`;
  };

  /* Reset a password → confirm (R24: it invalidates their current one) → the new
     temporary password, shown once in the handed card, stored nowhere. */
  const doResetPassword = async (email: string) => {
    setMenuFor('');
    const ok = await confirmDialog({
      title: `Reset ${displayName(email, profiles)}’s password?`,
      body: (
        <>
          A new temporary password is issued and shown once. <b>{displayName(email, profiles)}</b>{' '}
          must choose a new password at next sign-in; their current one stops working immediately.
          Roles and boards are unchanged.
        </>
      ),
      confirmLabel: 'Reset password',
      tone: 'caution',
    });
    if (!ok) return;
    try {
      const password = await resetPassword(email);
      if (password) setResetHanded({ email, password });
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  };

  /* Correct an address → the current one (read-only) + the new one (required,
     R10), with the consequence stated → changeEmail (server-side, admin-gated). */
  const doCorrectEmail = async (email: string) => {
    setMenuFor('');
    const next = await promptDialog({
      title: `Correct ${displayName(email, profiles)}’s email address`,
      body: (
        <>
          This changes the address <b>{displayName(email, profiles)}</b> signs in with and where
          their sign-in link is sent. Their roles, boards and history stay exactly as they are.
        </>
      ),
      current: { label: 'Current', value: email },
      label: 'New email address',
      placeholder: 'their@email.com',
      required: true,
      submitLabel: 'Change email',
    });
    if (next === null) return;
    const problem = await changeEmail(email, next);
    if (problem) setErr(problem);
    else setErr('');
  };

  /* Deactivate or reactivate → confirm (R24). Deactivating keeps every role and
     board; it is a reversible pause, not a removal. */
  const doSetSuspended = async (email: string, suspend: boolean) => {
    setMenuFor('');
    const ok = await confirmDialog({
      title: suspend
        ? `Deactivate ${displayName(email, profiles)}?`
        : `Reactivate ${displayName(email, profiles)}?`,
      body: suspend ? (
        <>
          <b>{displayName(email, profiles)}</b> will no longer be able to sign in. Every role and
          board they hold is kept, so turning them back on restores them exactly — this is a
          reversible pause, not a removal.
        </>
      ) : (
        <>
          <b>{displayName(email, profiles)}</b> will be able to sign in again, with the roles and
          boards they had.
        </>
      ),
      confirmLabel: suspend ? 'Deactivate' : 'Reactivate',
      tone: 'caution',
    });
    if (!ok) return;
    report(setSuspended(email, suspend));
  };

  /* The Copy-invitation note, naming the person's ACTUAL boards (#3). Falls back
     to the workspace when they are on none yet, so the note always says something
     true rather than naming whichever board happened to be open. */
  const copyInvitation = (email: string, isTheirAdmin: boolean) => {
    setMenuFor('');
    const names = isTheirAdmin
      ? ['every board']
      : boardsOpenTo(email, isTheirAdmin).map((p) => p.name);
    void navigator.clipboard
      .writeText(
        invitationText({
          from: displayName(myEmail, profiles),
          boardName: names.length ? names : meta.name || 'the board',
          target: signInTarget(),
          isTest: targetIsElsewhere(),
        })
      )
      .then(
        () => setCopied(email),
        () => setErr('Could not reach the clipboard — copy it from the box below.')
      );
  };

  const canAdmin = can(role, 'manageWorkspace');

  /*
   * ONE PERSON'S ROW — the roster card and, when open, the Manage detail. The
   * credential and danger actions live in the ⋯ menu (below), not in the detail,
   * so the detail is only ever about who they are and what they can reach.
   */
  const renderPerson = (e: string) => {
    const legacy = legacyRoles[e];
    const isTheirAdmin = access.admins.includes(e) || legacy === 'admin';
    const theirRole = isTheirAdmin ? 'admin' : (roles[e] ?? legacy ?? 'viewer');
    const profile = profiles[e] ?? {};
    const isMe = e === myEmail;
    const open = editing === e;
    const menuOpen = menuFor === e;
    const isVendorPerson = standingOf(e) === 'vendor';
    const suspended = !!profile.suspendedAt;
    const neverSignedIn = !profile.passwordSetAt;
    const name = displayName(e, profiles);
    const first = name.split(' ')[0] || name;
    const status = suspended
      ? { cls: 'suspended', word: 'Suspended' }
      : neverSignedIn
        ? { cls: 'invited', word: 'Invited' }
        : { cls: 'ok', word: 'Active' };

    return (
      <div key={e} className={`person-card ${open ? 'is-open' : ''} ${suspended ? 'is-suspended' : ''}`}>
        <div className="person-top">
          <span className="avatar-me person-av">{initials(e, profiles)}</span>
          <div className="person-id">
            <div className="person-name">
              {name}
              <VendorTag who={e} profiles={profiles} compact />
              {isMe && <span className="person-you">you</span>}
              {!isNamed(e, profiles) && <span className="person-unnamed">no name set</span>}
            </div>
            <div className="person-sub">
              {/* The email is the unique identity on a COLLAPSED roster row, so it
                  reads there; once the editor is OPEN it is shown and changeable in
                  Profile below, so it is dropped here to avoid saying it twice. The
                  leading separators follow whatever is actually first. */}
              {!open && e}
              {profile.title && (
                <span className="person-title">{open ? '' : ' · '}{profile.title}</span>
              )}
              {/* LAST SIGNED IN (people-access A) — a humane relative time, or "—"
                  when this person has never signed in (the time comes from the
                  admin-only RPC merged above; see the note there). */}
              <span
                className="person-lastin"
                title={lastSignIn.has(e.toLowerCase()) ? `Last signed in ${asDate(lastSignIn.get(e.toLowerCase())!)}` : undefined}
              >
                {(open && !profile.title) ? '' : ' · '}Last signed in {lastSignIn.has(e.toLowerCase()) ? relativeTime(lastSignIn.get(e.toLowerCase())!) : '—'}
              </span>
            </div>
          </div>

          <div className="person-meta">
            {/* STATUS at a glance — dot AND word, so it reads with colour off
                (R28/P7): green Active, amber Invited (never signed in), grey
                Suspended. */}
            <span className={`person-status ${status.cls}`}>
              <span className="dot" />
              {status.word}
            </span>
            <span
              className={`role-tag role-${theirRole} person-rt`}
              title={isTheirAdmin ? 'Workspace admin — every board' : `${ROLE_LABEL[theirRole]} by default`}
            >
              {ROLE_LABEL[theirRole]}
            </span>

            {open ? (
              <button className="btn btn-small" onClick={() => setEditing('')} aria-expanded>
                Done
              </button>
            ) : (
              <>
                <span className="person-reach">{reachLabel(e, isTheirAdmin, isVendorPerson)}</span>
                <span className="acct-wrap" ref={menuOpen ? menuWrapRef : undefined}>
                  <button
                    className={`person-kebab ${menuOpen ? 'is-on' : ''}`}
                    aria-haspopup="menu"
                    aria-expanded={menuOpen}
                    aria-label={`Account actions for ${name}`}
                    onClick={() => setMenuFor(menuOpen ? '' : e)}
                  >
                    <Icon name="ellipsis" size={18} />
                  </button>
                  {menuOpen && (
                    <div className="acct-menu" role="menu">
                      <div className="acct-lbl">Sign-in &amp; password</div>
                      {canAdmin && (
                        <button className="acct-item" role="menuitem" onClick={() => void doResetPassword(e)}>
                          <Icon name="lock" size={17} /> Reset password
                        </button>
                      )}
                      <button
                        className="acct-item"
                        role="menuitem"
                        disabled={inviting === e}
                        onClick={() => {
                          setMenuFor('');
                          void sendInvite(e);
                        }}
                      >
                        <Icon name="mail" size={17} />
                        {inviting === e ? 'Sending…' : invited[e] === 'sent' ? 'Sign-in link sent' : 'Send sign-in link'}
                      </button>
                      <button className="acct-item" role="menuitem" onClick={() => copyInvitation(e, isTheirAdmin)}>
                        <Icon name="document" size={17} />
                        {copied === e ? 'Invitation copied' : 'Copy invitation'}
                      </button>

                      <div className="acct-div" />
                      <div className="acct-lbl">Identity &amp; access</div>
                      {canAdmin && (
                        <button className="acct-item" role="menuitem" onClick={() => void doCorrectEmail(e)}>
                          <Icon name="pencil" size={17} /> Correct email address
                        </button>
                      )}
                      <button
                        className="acct-item"
                        role="menuitem"
                        onClick={() => {
                          setMenuFor('');
                          setEditing(e);
                        }}
                      >
                        <Icon name="users" size={17} />
                        {isVendorPerson ? (
                          <>
                            Manage boards <span className="rt">Vendor tier</span>
                          </>
                        ) : (
                          'Manage access & role'
                        )}
                      </button>
                      {canPreview(role, isTheirAdmin ? 'admin' : theirRole) && (
                        <button
                          className="acct-item"
                          role="menuitem"
                          onClick={() => {
                            setMenuFor('');
                            previewAs(e);
                            setView('board');
                          }}
                        >
                          <Icon name="eye" size={17} /> View as {first}
                          {isVendorPerson ? ' (vendor)' : ''}
                        </button>
                      )}

                      {canAdmin && !isMe && (
                        <>
                          <div className="acct-div" />
                          <button
                            className="acct-item is-danger"
                            role="menuitem"
                            onClick={() => void doSetSuspended(e, !suspended)}
                          >
                            <Icon name={suspended ? 'check' : 'blocked'} size={17} />
                            {suspended ? 'Reactivate' : 'Deactivate'}
                          </button>
                          <button
                            className="acct-item is-danger"
                            role="menuitem"
                            disabled={isTheirAdmin}
                            title={isTheirAdmin ? 'Admins can open every board — remove their admin first' : ''}
                            onClick={async () => {
                              setMenuFor('');
                              const ok = await confirmDialog({
                                title: `Remove ${name} from the workspace?`,
                                body: (
                                  <>
                                    <b>{name}</b> ({e}) loses access to every project immediately. Their
                                    comments stay.
                                  </>
                                ),
                                confirmLabel: 'Remove',
                                tone: 'danger',
                              });
                              if (ok) report(removePerson(e));
                            }}
                          >
                            <Icon name="trash" size={17} /> Remove from workspace
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </span>
              </>
            )}
          </div>
        </div>

        {open && (
          <div className="person-manage">
            {/* PROFILE — who they are. */}
            <div className="pm-sec">
              <p className="pm-sec-h">Profile</p>
              <div className="pm-grid">
                <label className="stack">
                  Name
                  <input
                    className="input"
                    defaultValue={profile.name ?? nameFromEmail(e)}
                    placeholder={nameFromEmail(e)}
                    onBlur={(ev) => void setPersonProfile(e, { name: ev.target.value.trim() })}
                  />
                </label>
                <label className="stack">
                  What they do here
                  <input
                    className="input"
                    defaultValue={profile.title ?? ''}
                    placeholder="e.g. Designer, or Advising on the API"
                    onBlur={(ev) => void setPersonProfile(e, { title: ev.target.value.trim() })}
                  />
                </label>
              </div>
              {/* EMAIL — shown here, changed deliberately. It is NOT a type-and-blur
                  field: an address change re-keys the person across ~30 tables and
                  auth, so it goes through the SAME confirmed, audited flow the ⋯ menu
                  uses (doCorrectEmail → changeEmail), never silently. */}
              <div className="stack pm-email" style={{ marginTop: 13 }}>
                Email address
                <div className="pm-email-row">
                  <span className="pm-email-val" title={e}>{e}</span>
                  <button
                    type="button"
                    className="btn btn-small pm-email-change"
                    onClick={() => void doCorrectEmail(e)}
                  >
                    Change
                  </button>
                </div>
              </div>
            </div>

            {/* ROLE & STANDING — what they are, and whether they are an admin. */}
            <div className="pm-sec">
              <p className="pm-sec-h">Role &amp; standing</p>
              <div className="pm-grid">
                <label className="stack">
                  <Info className="pm-lbl" title="Default role" what="The role this person gets on a board that has not set one of its own.">
                    Default role
                  </Info>
                  <select
                    value={isTheirAdmin ? 'manager' : theirRole}
                    disabled={isMe || isTheirAdmin}
                    title={
                      isMe
                        ? 'You cannot change your own role — it would lock you out'
                        : isTheirAdmin
                          ? 'Admins already have everything a project manager has'
                          : ''
                    }
                    onChange={(ev) => void setPersonRole(e, ev.target.value as ProjectRole)}
                  >
                    {PROJECT_ROLES.map((r) => (
                      <option key={r} value={r}>
                        {ROLE_LABEL[r]}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="stack">
                  <Info className="pm-lbl" title="Company role" what="Whether they are internal or an external party such as a vendor. It drives how their name is tagged everywhere.">
                    Company role
                  </Info>
                  <select
                    value={profile.companyRole ?? ''}
                    disabled={!canAdmin}
                    onChange={(ev) =>
                      void setPersonProfile(e, {
                        companyRole: (ev.target.value || null) as CompanyRole | null,
                      })
                    }
                  >
                    <option value="">Not set</option>
                    {COMPANY_ROLE_PICK.map((r) => (
                      <option key={r} value={r}>
                        {COMPANY_ROLE_LABEL[r]}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
              <label className="stack" style={{ marginTop: 13 }}>
                <Info className="pm-lbl" title="When they create a card" what="Which slot a card they raise puts them in — Responsible, Accountable, or just following.">
                  When they create a card
                </Info>
                <select
                  value={profile.cardDefaultStance ?? 'follow'}
                  disabled={!canAdmin}
                  onChange={(ev) =>
                    void setPersonProfile(e, { cardDefaultStance: ev.target.value as CardStance })
                  }
                >
                  {CARD_STANCE_ORDER.map((s) => (
                    <option key={s} value={s}>
                      {STANCE_LABEL[s]}
                    </option>
                  ))}
                </select>
              </label>

              {/* HOW THE THREE CONNECT (B-08) — only for a vendor, so the model
                  reads at the moment it applies. */}
              {profile.companyRole === 'vendor' && (
                <div className="vendor-connect">
                  <div className="vcx-h">How the three connect</div>
                  <p className="hint vcx-lead">
                    One attribute — the company role — drives both how this person reads and what you’d
                    grant them on a board.
                  </p>
                  <div className="vcx-row">
                    <div className="vcx-card">
                      <div className="vcx-top">
                        <span className="vcx-num">1</span>
                        <span className="vcx-t">Company role</span>
                      </div>
                      <p className="vcx-say">
                        Set here, once, in the workspace. <b>External / Vendor</b> says who they are.
                      </p>
                      <span className="vcx-chip-role">
                        <Icon name="shield" size={12} />
                        External / Vendor
                      </span>
                    </div>
                    <span className="vcx-arrow" aria-hidden="true">
                      <Icon name="arrow-right" size={22} />
                    </span>
                    <div className="vcx-card">
                      <div className="vcx-top">
                        <span className="vcx-num">2</span>
                        <span className="vcx-t">Vendor name-tag</span>
                      </div>
                      <p className="vcx-say">
                        Appears beside their name <b>everywhere</b> — card face, pickers, roster,
                        mentions, My Work.
                      </p>
                      <VendorTag who={e} profiles={profiles} />
                    </div>
                    <span className="vcx-arrow" aria-hidden="true">
                      <Icon name="arrow-right" size={22} />
                    </span>
                    <div className="vcx-card">
                      <div className="vcx-top">
                        <span className="vcx-num">3</span>
                        <span className="vcx-t">Vendor access tier</span>
                      </div>
                      <p className="vcx-say">
                        The <b>suggested</b> board role: work surface only. You still grant it per board.
                      </p>
                      <span className="input pa-role is-set vcx-chip-tier">
                        Vendor <Icon name="chevron" size={16} />
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {canAdmin && (
                <label className="stack" style={{ marginTop: 13 }}>
                  Also a workspace admin
                  <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <button
                      className={`switch ${isTheirAdmin ? 'on' : ''}`}
                      role="switch"
                      aria-checked={isTheirAdmin}
                      aria-label={`Make ${name} an admin`}
                      disabled={isMe || isVendorPerson}
                      title={
                        isMe
                          ? 'You cannot change your own admin rights'
                          : isVendorPerson
                            ? 'A vendor can never be a workspace admin'
                            : ''
                      }
                      onClick={() => report(setPersonAdmin(e, !isTheirAdmin))}
                    >
                      <span className="switch-knob" />
                    </button>
                    <span className="hint">
                      {isVendorPerson
                        ? 'A vendor can never be a workspace admin.'
                        : isTheirAdmin
                          ? 'Can add and remove people, and appoint other admins.'
                          : 'Off — they can be given a project role above instead.'}
                    </span>
                  </span>
                </label>
              )}
            </div>

            {/* BOARDS & TEAMS — what they can reach. */}
            <div className="pm-sec">
              <p className="pm-sec-h">Boards &amp; teams</p>
              <PersonAccess email={e} report={report} />
              <p className="pm-note">
                Reset password, correct email, send the sign-in link, deactivate or remove {first} from
                the <b>⋯</b> menu on their row — those are account actions, kept out of this access
                panel.
              </p>
            </div>
          </div>
        )}
      </div>
    );
  };

  /*
   * RENDERED INSTEAD OF THIS SCREEN, not over it. Adding somebody is now a page
   * of its own with four sections in it, and a dialog that size laid over a
   * list is a list nobody can read behind a form nobody can escape. The same
   * reasoning as the terms and the password gate.
   */
  if (addingSomeone) return <AddPerson onDone={() => setAddingSomeone(false)} />;

  if (!can(role, 'setRoles')) {
    return (
      <div className="bl-empty">
        Only a project manager or an admin can manage people and access.
        <div>
          <button className="btn btn-small admin-back" onClick={() => setView('board')}>
            Back to the board
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="settings">
      <div className="tabs" role="tablist" onKeyDown={tabKeys.onKeyDown}>
        {visibleTabs.map((t) => (
          <button
            key={t.id}
            role="tab"
            aria-selected={current === t.id}
            ref={tabKeys.setRef(t.id)}
            tabIndex={tabKeys.tabIndex(t.id)}
            className={`tab ${current === t.id ? 'is-on' : ''}`}
            onClick={() => setTab(t.id)}
          >
            {t.label}
            {t.id === 'requests' && pendingRequests > 0 && (
              <span className="seg-badge">{pendingRequests}</span>
            )}
          </button>
        ))}
      </div>

      {current === 'requests' && <RequestsQueue />}

      {current === 'people' && (
        <>
        <section className="panel">
          {/* A proper account-management header: who is here, and an Add that is
              pinned top-right so it is never scrolled to (Vinnie's pain). */}
          <div className="pa-head">
            <div>
              <h2>People &amp; access</h2>
              <p className="pa-sum">
                <b>{access.emails.length}</b> {access.emails.length === 1 ? 'person' : 'people'} ·{' '}
                {countAdmins} {countAdmins === 1 ? 'admin' : 'admins'} · {countMembers} internal{' '}
                {countMembers === 1 ? 'member' : 'members'} · {countVendors} external{' '}
                {countVendors === 1 ? 'vendor' : 'vendors'}
                {countInvited > 0 && (
                  <>
                    {' '}
                    · <b>{countInvited} invited</b>, not yet signed in
                  </>
                )}
              </p>
            </div>
            {canAdmin && (
              <div className="pa-add">
                <button className="btn btn-primary" onClick={() => setAddingSomeone(true)}>
                  <Icon name="plus" size={16} /> Add someone
                </button>
              </div>
            )}
          </div>

          {/* The search and a standing filter, together — find somebody, or narrow
              to one kind. */}
          <div className="pa-toolbar">
            <input
              className="input people-find"
              value={findPerson}
              onChange={(ev) => setFindPerson(ev.target.value)}
              placeholder="Find a person by name or address"
            />
            <div className="seg" role="tablist" aria-label="Filter people">
              {(
                [
                  ['all', 'All'],
                  ['admins', 'Admins'],
                  ['members', 'Members'],
                  ['vendors', 'Vendors'],
                  ['invited', 'Invited'],
                ] as const
              ).map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  className={`seg-btn ${band === key ? 'active' : ''}`}
                  aria-pressed={band === key}
                  onClick={() => setBand(key)}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {err && <div className="login-error">{err}</div>}

          {/* GROUPED Admins / Internal members / External vendors (B-03: the
              vendor band sits apart, so an outside supplier is never mistaken for
              staff). A band shows only when the current filter leaves it anyone. */}
          {visible.length === 0 ? (
            <p className="hint">
              {findPerson.trim() || band !== 'all'
                ? 'Nobody matches that.'
                : 'Nobody has been added to this workspace yet.'}
            </p>
          ) : (
            <>
              {admins.length > 0 && (
                <>
                  <div className="pa-band">
                    <span className="lbl">Admins</span>
                    <span className="ct">{admins.length}</span>
                    <span className="rule" />
                  </div>
                  <div className="people">{admins.map(renderPerson)}</div>
                </>
              )}
              {members.length > 0 && (
                <>
                  <div className="pa-band">
                    <span className="lbl">Internal members</span>
                    <span className="ct">{members.length}</span>
                    <span className="rule" />
                  </div>
                  <div className="people">{members.map(renderPerson)}</div>
                </>
              )}
              {vendors.length > 0 && (
                <>
                  <div className="pa-band">
                    <span className="lbl">External vendors</span>
                    <span className="ct">{vendors.length}</span>
                    <span className="rule" />
                  </div>
                  <div className="people">{vendors.map(renderPerson)}</div>
                </>
              )}
            </>
          )}

          <p className="hint">
            <strong>Send sign-in link</strong> emails a one-click link — there is no password to
            create or share. <strong>Copy invitation</strong> gives you a short note to send yourself,
            so the automated email is expected. Both are on the <b>⋯</b> menu on each person's row.
          </p>
          {Object.entries(invited).some(([, v]) => v !== 'sent') && (
            <div className="login-error">
              {Object.entries(invited)
                .filter(([, v]) => v !== 'sent')
                .map(([k, v]) => `${k}: ${v}`)
                .join(' · ')}
            </div>
          )}
        </section>

        {/* RESET PASSWORD — the new temporary password, shown ONCE (R24), in the
            same handed card the add flow uses. Stored nowhere; closing loses it. */}
        {resetHanded && (
          <div
            className="overlay overlay-confirm"
            onClick={() => {
              setResetHanded(null);
              setCopiedPw(false);
            }}
          >
            <div className="modal confirm" onClick={(ev) => ev.stopPropagation()}>
              <p className="confirm-title">
                New temporary password for {displayName(resetHanded.email, profiles)}
              </p>
              <div className="add-card handed">
                <p className="handed-value">{resetHanded.password}</p>
                <div className="handed-actions">
                  <button
                    className="btn"
                    onClick={() => {
                      void navigator.clipboard
                        ?.writeText(resetHanded.password)
                        .then(() => setCopiedPw(true), () => setCopiedPw(false));
                    }}
                  >
                    {copiedPw ? 'Copied' : 'Copy'}
                  </button>
                </div>
                <p className="handed-warn">
                  This is the only time it is shown. It is not saved anywhere and cannot be looked up —
                  if you lose it, issue a new one.
                </p>
                <p className="hint">
                  They must choose their own password at next sign-in before they can open anything.
                </p>
              </div>
              <div className="confirm-btns">
                <button
                  className="btn btn-primary"
                  onClick={() => {
                    setResetHanded(null);
                    setCopiedPw(false);
                  }}
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        )}
        {/* The ask-an-admin panel, for the board manager who is not a workspace
            admin. Filed against the board they have open — the one they run. */}
        {!can(role, 'manageWorkspace') && projectId && <RequestToAdd kind="person" boardId={projectId} />}
        </>
      )}

      {current === 'roles' && (
        <>
      <section className="panel">
        <h2>What each role can and cannot do</h2>

        <div className="role-cards">
          {ROLE_COLUMNS.map((r) => (
            <div key={r} className={`role-card ${r === role ? 'is-mine' : ''}`}>
              <div className="role-card-head">
                <span className={`role-tag role-${r}`}>{ROLE_LABEL[r]}</span>
                {r === role && <span className="person-you">you</span>}
              </div>
              <p className="role-card-blurb">{ROLE_BLURB[r]}</p>
            </div>
          ))}
        </div>

        <div className="perm-key">
          <span className="perm-key-item">
            <Icon name="check" size={12} className="perm-yes" /> can do this
          </span>
          <span className="perm-key-item">
            <Icon name="close" size={12} className="perm-no-icon" /> cannot do this
          </span>
        </div>

        <div className="perm-table" style={{ ['--perm-cols' as string]: ROLE_COLUMNS.length }}>
          <div className="perm-row perm-head">
            <span />
            {ROLE_COLUMNS.map((r) => (
              <span key={r} className={r === role ? 'perm-mine' : ''}>
                {ROLE_LABEL[r]}
              </span>
            ))}
          </div>
          {CAPABILITY_LABEL.map((c) => (
            <div key={c.key} className="perm-row">
              <span className="perm-cap">{c.label}</span>
              {ROLE_COLUMNS.map((r) => (
                <span
                  key={r}
                  className={r === role ? 'perm-mine' : ''}
                  title={`${ROLE_LABEL[r]} ${can(r, c.key) ? 'can' : 'cannot'}: ${c.label}`}
                >
                  {can(r, c.key) ? (
                    <Icon name="check" size={13} className="perm-yes" />
                  ) : (
                    <Icon name="close" size={12} className="perm-no-icon" />
                  )}
                  <span className="sr-only">{can(r, c.key) ? 'can' : 'cannot'}</span>
                </span>
              ))}
            </div>
          ))}
        </div>
      </section>

        </>
      )}

      {current === 'audit' && (
        <HistoryPanel
          audit={audit}
          auditOrigin={auditOrigin}
          profiles={profiles}
          boardNames={Object.fromEntries(projects.map((p) => [p.key, p.name]))}
          restoring={restoring}
          onRestore={(e) => {
            setRestoring(e.id);
            report(restoreIssue(e), () => setRestoring(''));
          }}
          /*
           * ── THE TRAIL LEAVING, WRITTEN INTO THE TRAIL — #306 ────────────
           *
           * "Who took a copy of the history, and when" is the first question
           * asked after anything goes wrong, and this is the one export that
           * could previously happen without any trace at all because it did
           * not exist.
           *
           * Fired and forgotten — the file is already on their machine and
           * nothing here can undo that. But NOT swallowed: writing it as
           * `.catch(() => {})` is the exact mistake made on the board export
           * on 29 August, where Vinnie pressed Export, saw no line, and had
           * nothing anywhere to say whether the write was refused or never
           * attempted.
           */
          onDownloaded={(count) => {
            void recordAudit(
              'access',
              `downloaded the history as a file — ${count} entr${count === 1 ? 'y' : 'ies'}`
            ).catch((e: unknown) =>
              remember(
                'broke',
                `the history was downloaded and History could not be told — ${(e as { message?: string })?.message || String(e)}`
              )
            );
          }}
        />
      )}

      {current === 'groups' && <Groups report={report} />}
    </div>
  );
}

/**
 * THE HISTORY TAB — its own component so it can be checked.
 *
 * It used to be written inline behind `current === 'audit'`, and the tab starts
 * on People — so a static render could never reach it, and nothing in the suite
 * ever had. That is how a `support` entry came to render with a blank badge for
 * as long as support visits have existed: the label map was missing an entry,
 * and the one screen that would have shown it was unreachable to every check.
 *
 * Vinnie found it on live, twice. The second time was after the label was fixed
 * — because a label nothing renders is not a fix.
 */
const METHOD_LABEL: Record<string, string> = {
  password: 'Password',
  link: 'Magic link',
  sso: 'SSO',
};

/**
 * The structured facts worth opening a line for — for now, a sign-in's device,
 * system, browser, method and the BOARD it was made on, each shown only when it
 * is known. A later migration adds a content change's target and what moved; this
 * returns the parts an entry has, in reading order.
 *
 * The BOARD, NOT THE ENVIRONMENT (people-access B). The sign-in line used to carry
 * `environment` (LIVE/UAT), which is redundant — for a customer it is always LIVE.
 * The audit is board-scoped, so the entry already knows which board it belongs to;
 * that is the fact worth showing, and it is passed in resolved to its name.
 */
export function signInFacts(d: AuditEntry['detail'], board?: string): Array<[string, string]> {
  if (!d) return [];
  const rows: Array<[string, string]> = [];
  if (d.deviceKind) rows.push(['Device', d.deviceKind]);
  if (d.os) rows.push(['Operating system', d.os]);
  if (d.browser) rows.push(['Browser', d.browser]);
  if (d.method) rows.push(['Sign-in method', METHOD_LABEL[d.method] ?? d.method]);
  if (board) rows.push(['Board', board]);
  return rows;
}

export function HistoryPanel({
  audit,
  auditOrigin,
  profiles,
  boardNames,
  restoring,
  onRestore,
  onDownloaded,
}: {
  audit: AuditEntry[];
  /* A board's key → its name, so the board filter reads "Core Systems
     Modernisation" rather than the short key "CSM". A key with no name (an
     archived or unreachable board) falls back to the key itself. */
  boardNames?: Record<string, string>;
  /* The admin-only origin of each sign-in, merged onto its line by auditId. Only
     an admin ever receives any — RLS hands a non-admin none — so a row that has
     one is a row the viewer is allowed to see it on. Optional: a caller that does
     not deal in origins (and none reach a non-admin) simply shows no origin. */
  auditOrigin?: AuditOrigin[];
  profiles: Record<string, { name?: string }>;
  restoring?: string;
  onRestore?: (entry: AuditEntry) => void;
  /* Told AFTER the file has been handed over, so it can be written into the
     trail by the caller — this component reaches nothing on its own, which is
     what lets a screen check draw it. */
  onDownloaded?: (count: number) => void;
}) {
  const [how, setHow] = useState<Sift>(NOTHING_SIFTED);
  /* Which folded lines have been opened. By the id of the newest entry in the
     run, which is the only thing about a run that is stable. */
  const [opened, setOpened] = useState<Record<string, boolean>>({});

  const kept = sift(audit, how);
  const lines = fold(kept);
  const buried = kept.length - lines.length;

  /* A sign-in's origin, found by the id of its line. Built once per render from
     the admin-only list; empty for a non-admin, so a lookup simply misses and no
     origin block is drawn. */
  const originOf = new Map((auditOrigin ?? []).map((o) => [o.auditId, o] as const));

  /*
   * ── AND THE SAME PAGER AS THE OTHER TWO TRAILS — #310 ───────────────────
   *
   * Vinnie named the board's Activity feed and Security activity: **"pages on
   * both, keep it consistent."** This is the third list of the same kind, and
   * leaving it scrolling while the other two page would be the inconsistency
   * he objected to, arriving from the other direction. Say so if it should
   * come back out.
   *
   * PAGED OVER THE LINES, NOT THE ENTRIES, so "50 per page" means fifty rows
   * on screen. Paging the entries would draw an unpredictable number of rows
   * per page, which is the one thing a page size is for.
   */
  const [size, setSize] = useState(A_PAGE);
  const [page, setPage] = useState(1);
  useEffect(
    () => setPage(1),
    [how.kind, how.who, how.securityOnly, how.screen, how.since, size]
  );
  const where = at(lines.length, size, page);
  const onPage = lines.slice(where.start, where.end);

  /* Which sign-in or change entry has been opened to its structured detail —
     the drill. By entry id, separate from the folded-run `opened` above: one
     reveals the entries behind a count, this reveals the facts behind a line. */
  const [drilled, setDrilled] = useState<Record<string, boolean>>({});

  /* The date-range control, held as a day count so the select stays simple; 0
     is "all time". `since` on the sift is derived from it at the moment it
     changes, which is the right behaviour for a filter — reopening it re-reads
     "now". */
  const [rangeDays, setRangeDays] = useState(0);
  const chooseRange = (days: number) => {
    setRangeDays(days);
    setHow({ ...how, since: days ? Date.now() - days * 86_400_000 : 0 });
  };

  /* Everybody who appears, so the list offers the people who are actually in
     this trail rather than everybody who has ever had an account. */
  const whoEver = [...new Set(audit.map((e) => e.who))].sort();
  const kindsEver = [...new Set(audit.map((e) => e.kind))].sort() as AuditKind[];
  /* The boards that actually appear in the trail — workspace-level entries (a
     sign-in, an access change) carry no project and show only under "Any board".
     Sorted by the name the filter shows, not the short key it stores. */
  const screensEver = ([...new Set(audit.map((e) => e.project).filter(Boolean))] as string[]).sort(
    (a, b) => (boardNames?.[a] ?? a).localeCompare(boardNames?.[b] ?? b)
  );
  const nameOf = (email: string) => displayName(email, profiles);

  /*
   * ── THE TRAIL AS A FILE ────────────────────────────────────────────────
   *
   * The same shape as the board export in Settings, including the part that
   * was got wrong there and fixed: the write to History is fired and forgotten
   * because the file has already been handed over — but its refusal is NOT
   * swallowed. A write that fails silently reads exactly like one never
   * attempted, which is the silence this whole screen exists to remove.
   */
  const download = () => {
    const blob = new Blob([asFile(kept, nameOf, originOf)], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `flightdeck-history-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
    onDownloaded?.(kept.length);
  };

  return (
      <section className="panel">
        <h2>History</h2>
        <p className="hint">
          Who did what, and when. Only admins can see this.{' '}
          <Info
            title="What this can and cannot prove"
            what={
              <>
                <strong>Most of this is written by the database itself</strong>, as each change
                lands — signing in and out, people, roles, teams, areas and sprints. Those cannot
                be skipped or refused: they are recorded whether the change was made in this app,
                by a script, or by somebody working in the database directly. And{' '}
                <strong>no entry can ever be changed or deleted</strong>, by anyone, including an
                admin.{' '}
                <br />
                <br />
                Three things are still recorded by the browser that did them, because they change
                no record for the database to notice: whether a sign-in was a new one or this
                browser checking in, the whole board downloaded as a file, and the approved copy
                of a document. <strong>For those three alone</strong>, an absence here is not
                proof that nothing happened.
              </>
            }
          >
            <span className="hint">How reliable is this?</span>
          </Info>
        </p>

        {/*
          * ── NARROWING IT DOWN — #306 ──────────────────────────────────────
          *
          * Counted rather than guessed at: 811 of 1,172 entries were settings
          * changes and 440 of those were one field. A trail nobody can read is
          * not a smaller problem than a trail nobody keeps.
          *
          * Security first, and as a switch rather than one option inside the
          * kind list, because it is the question this screen is opened to
          * answer. Everything it leaves out is a record of work; everything it
          * keeps answers who could do what, and did they.
          */}
        {audit.length > 0 && (
          <div className="audit-sift">
            <label className="audit-only">
              <input
                type="checkbox"
                checked={how.securityOnly}
                onChange={(e) => setHow({ ...how, securityOnly: e.target.checked })}
              />
              Security only
            </label>
            <select
              aria-label="Person"
              value={how.who}
              onChange={(e) => setHow({ ...how, who: e.target.value })}
            >
              <option value="">Everybody</option>
              {whoEver.map((w) => (
                <option key={w} value={w}>
                  {nameOf(w)}
                </option>
              ))}
            </select>
            <select
              aria-label="Board"
              value={how.screen}
              onChange={(e) => setHow({ ...how, screen: e.target.value })}
            >
              <option value="">Any board</option>
              {screensEver.map((p) => (
                <option key={p} value={p}>
                  {boardNames?.[p] ?? p}
                </option>
              ))}
            </select>
            <select
              aria-label="Kind"
              value={how.kind}
              onChange={(e) => setHow({ ...how, kind: e.target.value })}
            >
              <option value="">Everything that happened</option>
              {kindsEver.map((k) => (
                <option key={k} value={k}>
                  {AUDIT_LABEL[k]}
                </option>
              ))}
            </select>
            <select
              aria-label="Date range"
              value={rangeDays}
              onChange={(e) => chooseRange(Number(e.target.value))}
            >
              <option value={0}>All time</option>
              <option value={1}>Last 24 hours</option>
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
            </select>
            {/* SAYING WHAT IS BEING HELD BACK, which is the rule #294 settled:
                a screen that quietly shows less than it has is worse than one
                that shows too much. Both numbers, because they are different
                kinds of missing — one is filtered out, one is folded up. */}
            <span className="audit-count">
              {lines.length} of {audit.length}
              {buried > 0 && <> · {buried} folded in</>}
            </span>
            <PageSize size={size} onSize={setSize} />
            <button className="btn btn-small" onClick={download} disabled={kept.length === 0}>
              Download as a file
            </button>
          </div>
        )}

        {audit.length === 0 ? (
          <div className="bl-empty">
            Nothing recorded yet. Sign-ins and changes to people, roles and projects appear here
            from now on.
          </div>
        ) : lines.length === 0 ? (
          /* An empty answer is an assertion — the lesson of #308. It has to be
             clear that this is a filter and not an empty trail. */
          <div className="bl-empty">
            Nothing here matches. {audit.length} entr{audit.length === 1 ? 'y is' : 'ies are'}{' '}
            recorded — widen the filters above to see them.
          </div>
        ) : (
          <div className="bl-rows">
            {onPage.map(({ entry: e, what, times, behind, wentNowhere, oneField }) => {
              const facts = signInFacts(
                e.detail,
                e.project ? (boardNames?.[e.project] ?? e.project) : undefined,
              );
              // A content change (a setting, a rename, a date moved) opens to its
              // structured from → to; a sign-in opens to its session. Only one of
              // the two is ever set on a given row.
              const change = facts.length === 0 ? changeFacts(e) : null;
              const canOpen = facts.length > 0 || change !== null;
              // The admin-only network origin of this sign-in, if the viewer is an
              // admin and we captured one. Present only on sign-in lines.
              const origin = originOf.get(e.id);
              return (
              <article key={e.id} className="audit-row">
                <span className={`audit-kind audit-${e.kind}`}>{AUDIT_LABEL[e.kind as keyof typeof AUDIT_LABEL]}</span>
                <div className="audit-main">
                  <div className="audit-line">
                    <span className="audit-who">{displayName(e.who, profiles)}</span>
                    <VendorTag who={e.who} profiles={profiles} compact />{' '}
                    <span className="audit-what">{what}</span>{' '}
                    {e.detail?.newDevice && <span className="audit-flag">New device</span>}{' '}
                    {times > 1 && (
                      <>
                        {/* THE COUNT IS NOT DECORATION. A line standing for
                            eleven entries and a line standing for one must not
                            look the same, or folding has quietly become
                            hiding. */}
                        <button
                          className="audit-times"
                          aria-expanded={Boolean(opened[e.id])}
                          onClick={() => setOpened({ ...opened, [e.id]: !opened[e.id] })}
                        >
                          {/* NOBODY EDITED A SIGN-IN. Since security events
                              fold too, the word has to follow what the run
                              actually is — one field worked through, or the
                              same thing happening again. */}
                          {times} {oneField ? 'edits' : 'times'}
                        </button>{' '}
                        {/* A REVERSAL IS THE ONE THING FOLDING COULD HIDE.
                            Changed and changed back reads as no change at all
                            once the middle is folded away, so it is said. */}
                        {wentNowhere && <span className="audit-nowhere">ended where it started</span>}
                      </>
                    )}
                  </div>
                  <div className="audit-sub">
                    {e.who}
                    {e.project && <> · {boardNames?.[e.project] ?? e.project}</>}
                  </div>
                  {times > 1 && opened[e.id] && (
                    <ol className="audit-behind">
                      {behind.map((b) => (
                        <li key={b.id}>
                          <span className="audit-what">{b.what}</span>
                          <span className="bl-time" title={new Date(b.at).toString()}>
                            {historyTime(b.at)}
                          </span>
                        </li>
                      ))}
                    </ol>
                  )}
                  {/* THE DRILL — the facts behind the sentence, opened in place
                      (no modal, no internal scroll — R1/R14). A read-only
                      expansion of a recorded entry; nothing here edits the
                      trail. For now a sign-in's device and method; a content
                      change's target and move follow in a later increment. */}
                  {drilled[e.id] && facts.length > 0 && (
                    <dl className="audit-detail">
                      <div className="audit-detail-head">Session</div>
                      {facts.map(([k, v]) => (
                        <div className="audit-detail-row" key={k}>
                          <dt>{k}</dt>
                          <dd>{v}</dd>
                        </div>
                      ))}
                      {e.detail?.newDevice && (
                        <div className="audit-detail-row">
                          <dt>New device</dt>
                          <dd>
                            <span className="audit-flag">First seen today</span>
                          </dd>
                        </div>
                      )}
                    </dl>
                  )}
                  {/* THE ADMIN-ONLY NETWORK ORIGIN — a bordered sub-panel, not a
                      rail (R12), tagged with a lock. Present only when the viewer
                      is an admin and we captured one, so its mere presence is the
                      permission check. Never shown to the person who signed in. */}
                  {drilled[e.id] && origin && (
                    <div className="audit-origin">
                      <div className="audit-origin-head">
                        <span className="audit-origin-tag">
                          <Icon name="lock" /> Admin only
                        </span>
                        <span className="audit-origin-title">Location &amp; network</span>
                      </div>
                      <dl className="audit-origin-body">
                        <div className="audit-detail-row">
                          <dt>IP address</dt>
                          <dd>{origin.ip || 'Not recorded'}</dd>
                        </div>
                        <div className="audit-detail-row">
                          <dt>Location</dt>
                          <dd>
                            {originPlace(origin) ? (
                              <>
                                {originPlace(origin)}{' '}
                                <span className="audit-approx">(approximate)</span>
                              </>
                            ) : (
                              'Could not be placed'
                            )}
                          </dd>
                        </div>
                      </dl>
                      <p className="audit-origin-note">
                        IP address and approximate location are kept for{' '}
                        <strong>90 days</strong>, then permanently removed. Visible to
                        workspace admins only — never shown to the person who signed in.
                      </p>
                    </div>
                  )}
                  {drilled[e.id] && change && (
                    <dl className="audit-detail">
                      <div className="audit-detail-head">What changed</div>
                      <div className="audit-detail-row">
                        <dt>Field</dt>
                        <dd>{change.field}</dd>
                      </div>
                      <div className="audit-detail-row">
                        <dt>Change</dt>
                        <dd className="audit-move">
                          <span className="audit-val">{change.from}</span>
                          <span className="audit-arrow" aria-hidden="true">→</span>
                          <span className="audit-val">{change.to}</span>
                        </dd>
                      </div>
                      <div className="audit-detail-row">
                        <dt>By</dt>
                        <dd>
                          {displayName(e.who, profiles)}
                          <VendorTag who={e.who} profiles={profiles} compact /> · {e.who}
                        </dd>
                      </div>
                      <div className="audit-detail-row">
                        <dt>Where</dt>
                        <dd>{e.project ?? 'Workspace'}</dd>
                      </div>
                      <div className="audit-detail-row">
                        <dt>When</dt>
                        <dd>
                          {asDate(e.at)},{' '}
                          {new Date(e.at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                        </dd>
                      </div>
                    </dl>
                  )}
                </div>
                {/* A record you can act on. Kept next to the entry rather
                    than on a screen of its own: the moment somebody wants
                    this is the moment they are looking at the deletion. */}
                {issueFromSnapshot(e.snapshot) && (
                  <button
                    className="btn btn-small"
                    disabled={restoring === e.id}
                    onClick={() => onRestore?.(e)}
                  >
                    {restoring === e.id ? 'Putting it back…' : 'Restore'}
                  </button>
                )}
                {/* A trail is read the same way a history is — you are
                    placing an entry against a day you remember, not asking
                    how many days have passed. The full stamp stays on hover
                    for the cases where the exact second matters. */}
                {/* The disclosure caret — only where there is something to
                    open. 18px, rotates to point down when open (R5/R6: no
                    hairline marks). */}
                {canOpen && (
                  <button
                    className="audit-open"
                    aria-expanded={Boolean(drilled[e.id])}
                    aria-label={drilled[e.id] ? 'Hide details' : 'Show details'}
                    onClick={() => setDrilled({ ...drilled, [e.id]: !drilled[e.id] })}
                  >
                    <span className={`audit-caret${drilled[e.id] ? ' is-open' : ''}`} aria-hidden="true">
                      <Icon name="chevron" size={18} />
                    </span>
                  </button>
                )}
                <span className="bl-time" title={new Date(e.at).toString()}>
                  {historyTime(e.at)}
                </span>
              </article>
              );
            })}
          </div>
        )}
        <Pages total={lines.length} size={size} page={where.page} onPage={setPage} what="lines" />
      </section>
  );
}
